/**
 * 二叉堆优先队列，排序键为 (time, seq) 二元组（设计文档 3.1 / event-queue.ts）。
 *
 * 同时刻事件按入队序 seq 处理 —— 这是 AC-F006-01（同种子完全复现）的必要条件。
 */
import type { SimEvent } from './events';

function comesBefore(a: SimEvent, b: SimEvent): boolean {
  return a.time < b.time || (a.time === b.time && a.seq < b.seq);
}

export class EventQueue {
  private readonly heap: SimEvent[] = [];
  private seq = 0;

  get size(): number {
    return this.heap.length;
  }

  /** 清空并重置序号（reset 时复刻确定性序列）。 */
  clear(): void {
    this.heap.length = 0;
    this.seq = 0;
  }

  /**
   * 入队一个事件。调用方通过 build 闭包构造事件体（含载荷），
   * 队列负责盖 time/seq 戳，保证 seq 单调。
   */
  enqueue(time: number, build: (time: number, seq: number) => SimEvent): SimEvent {
    if (!Number.isInteger(time)) {
      throw new Error(`EventQueue.enqueue: time must be integer ms, got ${time}`);
    }
    const ev = build(time, this.seq++);
    this.heap.push(ev);
    this.siftUp(this.heap.length - 1);
    return ev;
  }

  peek(): SimEvent | undefined {
    return this.heap[0];
  }

  pop(): SimEvent | undefined {
    const top = this.heap[0];
    if (top === undefined) return undefined;
    const last = this.heap.pop()!;
    if (this.heap.length > 0) {
      this.heap[0] = last;
      this.siftDown(0);
    }
    return top;
  }

  private siftUp(i: number): void {
    const item = this.heap[i]!;
    while (i > 0) {
      const parentIdx = (i - 1) >> 1;
      const parent = this.heap[parentIdx]!;
      if (comesBefore(item, parent)) {
        this.heap[i] = parent;
        i = parentIdx;
      } else {
        break;
      }
    }
    this.heap[i] = item;
  }

  private siftDown(i: number): void {
    const n = this.heap.length;
    const item = this.heap[i]!;
    for (;;) {
      let child = 2 * i + 1; // 左子
      if (child >= n) break;
      // 取较小子节点
      if (child + 1 < n && comesBefore(this.heap[child + 1]!, this.heap[child]!)) child++;
      // 与被下沉的缓存项比较（非 heap[i]），hole 法
      if (comesBefore(this.heap[child]!, item)) {
        this.heap[i] = this.heap[child]!;
        i = child;
      } else {
        break;
      }
    }
    this.heap[i] = item;
  }
}
